import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { useDispatch } from 'react-redux'
import { Cover, Stack, Button } from 'design-system'

import useBoardId from 'src/hooks/useBoardId'
import useApolloQuery, { isErr, isLoading } from 'src/hooks/useApolloQuery'
import {
  ARCHIVED_BOARD,
  UNARCHIVE_BOARD
} from 'src/graphql/board/archive-board'
import { useRouter } from 'src/lib/router'
import { isBoardAdmin } from 'src/lib/board/member'
import Avatar from 'src/components/user/v2/avatar'
import { setBanner } from 'src/store/action-methods/banner'
import { UnlinkCommunityModal } from 'src/components/board/settings/unlink-community-modal'

import { ArchivedBoardStudentView } from './student-view'
import { ArchivedBoardAdminView } from './admin-view'
import { ArchivedBoardBasicInfo } from './basic-info'
import useNetworkMember from 'src/hooks/use-network-member'

export const ArchivedBoardView = () => {
  const boardId = useBoardId()
  const router = useRouter()
  const dispatch = useDispatch()
  const [open, setOpen] = useState<boolean>(false)

  const res = useApolloQuery(ARCHIVED_BOARD, { variables: { boardId } })
  const networkMemberRes = useNetworkMember()
  const [unarchiveBoard] = useMutation(UNARCHIVE_BOARD)

  if (isLoading(res) || isLoading(networkMemberRes)) {
    return <div>Loading...</div>
  }

  if (isErr(res) || isErr(networkMemberRes)) {
    return <div>Error...</div>
  }

  const board = res.data.findBoard
  const { networkUser } = networkMemberRes.data.loggedInUser
  const { loggedInBoardUser, archivedBy, network, ltiIds, lti3Integrations } =
    board
  const { title: networkTitle } = network
  const canAdminBoard = isBoardAdmin({
    boardUser: loggedInBoardUser,
    networkUser
  })

  const hasLTIIntegration = ltiIds.length > 0 || lti3Integrations.length > 0

  const onModalClose = () => setOpen(false)
  const onModalOpen = () => setOpen(true)

  const onUnarchive = async () => {
    try {
      await unarchiveBoard({ variables: { boardId } })
      setBanner({
        open: true,
        msg: 'This community has been successfully un-archived.',
        assertive: true
      })(dispatch)
      setTimeout(() => router.networkPush(`/community/${boardId}`), 1500)
    } catch (e) {
      setBanner({
        open: true,
        error: true,
        msg: 'We were unable to un-archive this community.',
        assertive: true
      })(dispatch)
    }
  }

  return (
    <Cover fullSize className="h-screen">
      <Stack
        className="w-11/12 overflow-y-auto rounded-lg bg-white p-6 text-center md:w-8/12 xl:w-5/12"
        gap={2}>
        <div className="mx-auto h-auto w-full pb-4">
          <Avatar
            user={loggedInBoardUser.user}
            size="small"
            admin={canAdminBoard}
          />
          <h3 className="py-4">This community has been archived</h3>
          <ArchivedBoardBasicInfo board={board} />
          {canAdminBoard && archivedBy ? (
            <ArchivedBoardAdminView
              networkTitle={networkTitle}
              archivedBy={archivedBy}
              board={board}
            />
          ) : (
            <ArchivedBoardStudentView networkTitle={networkTitle} />
          )}
          <div className="text-left text-slate-500">
            For additional information, please read{' '}
            <a
              href="https://support.yellowdig.co/hc/en-us/articles/24640813807380"
              target="_blank"
              rel="noreferrer">
              this article
            </a>{' '}
            in our Knowledge Base.
            <br />
            <br />
            For additional help please email us at{' '}
            <a href="mailto:support@yellowdig.com">support@yellowdig.com</a>
          </div>
        </div>
        <div className="flex flex-col gap-y-4 md:flex-row md:justify-center md:gap-x-4">
          {canAdminBoard ? (
            <Button
              buttonSize="xs"
              buttonType="secondary"
              fullWidth
              onClick={onUnarchive}>
              Un-Archive this community
            </Button>
          ) : null}
          <Button
            buttonSize="xs"
            buttonType="primary"
            fullWidth
            onClick={() => {
              router.networkPush('/communities')
            }}>
            Go Home
          </Button>
          {canAdminBoard && hasLTIIntegration ? (
            <Button
              buttonSize="xs"
              buttonType="red"
              fullWidth
              onClick={onModalOpen}>
              Unlink from LMS
            </Button>
          ) : null}
        </div>
      </Stack>
      <UnlinkCommunityModal
        open={open}
        onModalClose={onModalClose}
        onUnlinkSuccess={res.refetch}
      />
    </Cover>
  )
}
